import { useEffect, useMemo, useState } from 'react';
import {
  listCalendarThemes, createCalendarTheme, updateCalendarTheme, deleteCalendarTheme,
  type CalThemeRow,
} from '../../../api/calendarThemes';
import {
  Palette, Plus, Trash2, Eye, EyeOff, Save, AlertTriangle, Lock, Sun, Moon,
} from '../../../icons';
import './CalendarTheme.css';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

/**
 * 캘린더 테마 관리 (2026-06-07 T3) — admin_calendar_themes CRUD + 16색 편집기.
 *
 * 좌: 테마 목록(선택/추가/삭제). 우: 선택 테마 편집(16개 --cal-* 색 picker + light/dark 미니 프리뷰 + 대비 경고).
 * 빌트인 default 는 편집 잠금(가드). 그 외 빌트인은 tokens 수정 가능·삭제 불가.
 */

interface FieldDef { key: string; label: string }
const SURFACE_FIELDS: FieldDef[] = [
  { key: '--cal-grid-bg', label: '그리드 배경' },
  { key: '--cal-cell-bg', label: '셀 배경' },
  { key: '--cal-cell-other-bg', label: '타월 셀' },
  { key: '--cal-today-bg', label: '오늘 셀' },
  { key: '--cal-border', label: '테두리' },
  { key: '--cal-header-bg', label: '요일 헤더' },
  { key: '--cal-text', label: '글자' },
  { key: '--cal-muted', label: '보조 글자' },
  { key: '--cal-accent', label: '강조(오늘)' },
];
const CATEGORY_FIELDS: FieldDef[] = [
  { key: '--cal-type-meeting', label: '회의' },
  { key: '--cal-type-personal', label: '개인' },
  { key: '--cal-type-external', label: '외부' },
  { key: '--cal-type-training', label: '교육' },
  { key: '--cal-type-todo', label: '할일' },
  { key: '--cal-type-anniversary', label: '기념일' },
  { key: '--cal-type-vacation', label: '휴가' },
];
// picker 초기값(토큰 미설정 시) — 시각 기준만, 저장 안 됨.
const PICKER_DEFAULT: Record<string, string> = {
  '--cal-grid-bg': '#ffffff', '--cal-cell-bg': '#ffffff', '--cal-cell-other-bg': '#f8fafc',
  '--cal-today-bg': '#eef2ff', '--cal-border': '#e5e7eb', '--cal-header-bg': '#f3f4f6',
  '--cal-text': '#111827', '--cal-muted': '#6b7280', '--cal-accent': '#3b82f6',
  '--cal-type-meeting': '#3b82f6', '--cal-type-personal': '#10b981', '--cal-type-external': '#ec4899',
  '--cal-type-training': '#8b5cf6', '--cal-type-todo': '#f97316', '--cal-type-anniversary': '#ef4444', '--cal-type-vacation': '#14b8a6',
};
const HEX_RE = /^#[0-9a-fA-F]{3}$|^#[0-9a-fA-F]{6}$/;

function hexToLum(hex: string): number | null {
  const m = hex.trim().match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/);
  if (!m || !m[1]) return null;
  let h = m[1];
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const [r, g, b] = [0, 2, 4].map((i) => parseInt(h.slice(i, i + 2), 16) / 255);
  const lin = (c: number) => (c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4));
  return 0.2126 * lin(r!) + 0.7152 * lin(g!) + 0.0722 * lin(b!);
}
function contrastRatio(a: string, b: string): number | null {
  const la = hexToLum(a); const lb = hexToLum(b);
  if (la == null || lb == null) return null;
  const hi = Math.max(la, lb); const lo = Math.min(la, lb);
  return (hi + 0.05) / (lo + 0.05);
}

function MiniPreview({ tokens, mode, large }: { tokens: Record<string, string>; mode: 'light' | 'dark'; large?: boolean }) {
  const gridBg = tokens['--cal-grid-bg'] ?? (mode === 'dark' ? '#0f172a' : '#ffffff');
  const cellBg = tokens['--cal-cell-bg'] ?? (mode === 'dark' ? '#1e293b' : '#ffffff');
  const todayBg = tokens['--cal-today-bg'] ?? (mode === 'dark' ? '#1e3a8a' : '#eef2ff');
  const border = tokens['--cal-border'] ?? (mode === 'dark' ? '#334155' : '#e5e7eb');
  const headerBg = tokens['--cal-header-bg'] ?? (mode === 'dark' ? '#0f172a' : '#f3f4f6');
  const accent = tokens['--cal-accent'] ?? PICKER_DEFAULT['--cal-accent'];
  const bars = ['--cal-type-meeting', '--cal-type-personal', '--cal-type-vacation', '--cal-type-todo'];
  return (
    <div className={`caltheme-mini caltheme-mini--${mode}${large ? ' caltheme-mini--lg' : ''}`} style={{ background: gridBg, borderColor: border }}>
      <div className="caltheme-mini-head" style={{ background: headerBg }}>
        <span className="caltheme-mini-dot" style={{ background: accent }} />
      </div>
      <div className="caltheme-mini-grid">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="caltheme-mini-cell" style={{ background: i === 2 ? todayBg : cellBg, borderColor: border }} />
        ))}
      </div>
      <div className="caltheme-mini-bars">
        {bars.map((k) => <span key={k} className="caltheme-mini-bar" style={{ background: tokens[k] ?? PICKER_DEFAULT[k] }} />)}
      </div>
    </div>
  );
}

export function CalendarThemeSection() {
  const [themes, setThemes] = useState<CalThemeRow[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [draftKey, setDraftKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [tokens, setTokens] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const selected = themes.find((t) => t.id === selectedId) ?? null;
  const isDefault = selected?.theme_key === 'default';
  const locked = isDefault; // default 는 편집 잠금

  async function reload() {
    try { setThemes(await listCalendarThemes()); } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { void reload(); }, []);

  function loadInto(t: CalThemeRow) {
    setCreating(false); setSelectedId(t.id);
    setDraftKey(t.theme_key); setName(t.name); setDescription(t.description ?? '');
    setTokens({ ...t.tokens }); setError(null);
  }
  function startNew() {
    setCreating(true); setSelectedId(null);
    setDraftKey(''); setName(''); setDescription(''); setTokens({}); setError(null);
  }

  function setField(key: string, value: string | null) {
    setTokens((prev) => {
      const next = { ...prev };
      if (value == null) delete next[key];
      else next[key] = value;
      return next;
    });
  }

  const contrastWarn = useMemo(() => {
    const r = contrastRatio(tokens['--cal-text'] ?? '', tokens['--cal-cell-bg'] ?? '');
    return r != null && r < 4.5 ? r : null;
  }, [tokens]);
  const badHex = useMemo(
    () => Object.entries(tokens).filter(([, v]) => !HEX_RE.test(v) && !/^var\(--[a-z0-9-]+\)$/.test(v)).map(([k]) => k),
    [tokens],
  );

  async function save() {
    setError(null);
    if (!name.trim()) { setError('이름은 필수입니다.'); return; }
    if (badHex.length) { setError(`형식 오류 토큰: ${badHex.join(', ')}`); return; }
    setBusy(true);
    try {
      if (creating) {
        if (!/^[a-z0-9][a-z0-9_-]{1,39}$/.test(draftKey)) { setError('theme_key 는 소문자/숫자/-/_ 2~40자.'); setBusy(false); return; }
        await createCalendarTheme({ theme_key: draftKey, name: name.trim(), description: description.trim() || null, tokens });
      } else if (selected) {
        await updateCalendarTheme(selected.id, { name: name.trim(), description: description.trim() || null, ...(locked ? {} : { tokens }) });
      }
      await reload();
      setCreating(false);
    } catch (e) {
      const ax = e as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message ?? (e as Error).message);
    } finally { setBusy(false); }
  }

  async function toggleEnabled(t: CalThemeRow) {
    try { await updateCalendarTheme(t.id, { enabled: !t.enabled }); await reload(); }
    catch (e) { setError((e as Error).message); }
  }
  async function remove(t: CalThemeRow) {
    if (!confirm(`테마 "${t.name}" 를 삭제할까요?`)) return;
    try { await deleteCalendarTheme(t.id); if (selectedId === t.id) startNew(); await reload(); }
    catch (e) {
      const ax = e as { response?: { data?: { message?: string } } };
      setError(ax.response?.data?.message ?? (e as Error).message);
    }
  }

  const editing = creating || selected;

  return (
    <section className="svc-cat svc-cat--calendar caltheme-section">
      <ConsoleScreenHead
        title="캘린더 테마"
        kpi={themes.length > 0 ? `${themes.length}개` : undefined}
        desc="캘린더 화면·대시보드 위젯에 적용되는 색 테마를 관리합니다."
      />

      {error && !editing && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}

      <div className="caltheme-layout">
        <aside className="caltheme-list">
          <button type="button" className="caltheme-new-btn" onClick={startNew}>
            <Plus size={15} /> 새 테마
          </button>
          {themes.map((t) => {
            const active = selectedId === t.id;
            return (
              <div key={t.id} className={`caltheme-card${active ? ' is-selected' : ''}`}>
                <button type="button" className="caltheme-card__pick" onClick={() => loadInto(t)}>
                  <MiniPreview tokens={t.tokens} mode="light" />
                  <span className="caltheme-card__meta">
                    <span className="caltheme-card__name">{t.name}</span>
                    {t.is_builtin && <em className="caltheme-badge">빌트인</em>}
                  </span>
                </button>
                <div className="caltheme-card__actions">
                  <button
                    type="button"
                    className={`caltheme-icon-btn${t.enabled ? ' is-on' : ''}`}
                    onClick={() => void toggleEnabled(t)}
                    title={t.enabled ? '노출 중 — 클릭 시 숨김' : '숨김 — 클릭 시 노출'}
                    aria-pressed={t.enabled}
                  >
                    {t.enabled ? <Eye size={15} /> : <EyeOff size={15} />}
                  </button>
                  {!t.is_builtin && (
                    <button
                      type="button"
                      className="caltheme-icon-btn caltheme-icon-btn--danger"
                      onClick={() => void remove(t)}
                      title="삭제"
                      aria-label="삭제"
                    >
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </aside>

        <div className="caltheme-editor">
          {!editing && (
            <div className="caltheme-empty">
              <span className="caltheme-empty__icon"><Palette size={26} /></span>
              <p>좌측에서 테마를 선택하거나 <b>새 테마</b>를 만드세요.</p>
            </div>
          )}
          {editing && (
            <div className="caltheme-panel">
              <div className="caltheme-field-row">
                {creating && (
                  <label className="caltheme-field">
                    <span>theme_key</span>
                    <input value={draftKey} onChange={(e) => setDraftKey(e.target.value)} placeholder="my-theme" />
                  </label>
                )}
                <label className="caltheme-field">
                  <span>이름</span>
                  <input value={name} onChange={(e) => setName(e.target.value)} placeholder="테마 이름" />
                </label>
                <label className="caltheme-field caltheme-field--grow">
                  <span>설명</span>
                  <input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="(선택)" />
                </label>
              </div>

              {locked && <p className="caltheme-lock"><Lock size={13} /> 기본 테마는 색을 편집할 수 없습니다 (이름·설명만 수정 가능).</p>}

              {!locked && (
                <>
                  <div className="caltheme-previews">
                    <figure className="caltheme-prev">
                      <figcaption className="caltheme-prev__label"><Sun size={13} /> 라이트</figcaption>
                      <MiniPreview tokens={tokens} mode="light" large />
                    </figure>
                    <figure className="caltheme-prev">
                      <figcaption className="caltheme-prev__label"><Moon size={13} /> 다크</figcaption>
                      <MiniPreview tokens={tokens} mode="dark" large />
                    </figure>
                  </div>
                  {contrastWarn != null && (
                    <p className="caltheme-warn">
                      <AlertTriangle size={14} /> 글자/셀 배경 대비가 낮습니다 (비율 {contrastWarn.toFixed(1)} &lt; 4.5). 가독성을 확인하세요.
                    </p>
                  )}

                  <div className="caltheme-group">
                    <div className="caltheme-group-title">표면</div>
                    <div className="caltheme-fields">
                      {SURFACE_FIELDS.map((f) => <ColorField key={f.key} f={f} tokens={tokens} setField={setField} />)}
                    </div>
                  </div>
                  <div className="caltheme-group">
                    <div className="caltheme-group-title">카테고리 색</div>
                    <div className="caltheme-fields">
                      {CATEGORY_FIELDS.map((f) => <ColorField key={f.key} f={f} tokens={tokens} setField={setField} />)}
                    </div>
                  </div>
                </>
              )}

              {error && <p className="caltheme-error" role="alert">{error}</p>}
              <div className="caltheme-actions">
                <button type="button" className="svc-btn svc-btn--primary" disabled={busy} onClick={() => void save()}>
                  <Save size={13} /> {busy ? '저장 중…' : creating ? '생성' : '저장'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function ColorField({ f, tokens, setField }: { f: FieldDef; tokens: Record<string, string>; setField: (k: string, v: string | null) => void }) {
  const has = f.key in tokens;
  const val = tokens[f.key] ?? '';
  const pickerVal = HEX_RE.test(val) ? val : PICKER_DEFAULT[f.key] ?? '#000000';
  return (
    <div className={`caltheme-cf${has ? '' : ' caltheme-cf--off'}`}>
      <label className="caltheme-cf-chk" title="이 색을 테마에 포함(끄면 기본값 사용)">
        <input type="checkbox" checked={has} onChange={(e) => setField(f.key, e.target.checked ? pickerVal : null)} />
        <span className="caltheme-cf-swatch" style={{ background: has ? pickerVal : undefined }} />
        <span className="caltheme-cf-name">{f.label}</span>
      </label>
      <input type="color" className="caltheme-cf-color" value={pickerVal} disabled={!has} onChange={(e) => setField(f.key, e.target.value)} />
      <input
        className="caltheme-cf-hex" value={val} disabled={!has}
        placeholder="#RRGGBB" maxLength={20}
        onChange={(e) => setField(f.key, e.target.value)}
      />
    </div>
  );
}
